// Dashboard: the creator's notes inbox (wave 13, pillar 1; community GET /v1/games/:game/notes). Notes from players
// after a session, grouped by chip and version with similar notes folded under the first as "me too", their
// screenshots, and the loop's two actions: hand them to your agent (copy the markdown export, or thrdm feedback pull)
// and mark them addressed in a version (the author hears once it's live).

import { api, ApiError, API_BASE, accessToken } from "../api.ts";
import type { Game } from "../catalogue.ts";
import { fmtAgo, safeMediaUrl } from "../clips.ts";
import { clear, h, section, toast } from "../dom.ts";
import { CHIP_LABEL, CHIPS, ensureNotesStyle, FEEL_LABEL, vLabel, type Chip, type Note } from "../notes.ts";
import { nameSpan } from "./game.ts";

export interface Inbox {
  game_id: string;
  counts: { open: number; addressed: number; wontfix: number };
  groups: { chips: { chip: Chip | "general"; notes: number }[]; versions: { version: string; notes: number }[]; feel: Record<string, number> };
  notes: Note[];
}

export interface InboxFilter {
  status: "open" | "addressed" | "wontfix" | "all";
  version: string;
  chip: Chip | "";
}

export function inboxQuery(f: InboxFilter): string {
  const q = new URLSearchParams({ status: f.status });
  if (f.version) q.set("version", f.version);
  if (f.chip) q.set("chip", f.chip);
  return q.toString();
}

/** The leads in order, each followed by the notes folded under it (same_as). */
export function threaded(notes: Note[]): { note: Note; folded: boolean }[] {
  const under = new Map<string, Note[]>();
  for (const n of notes) if (n.same_as) under.set(n.same_as, [...(under.get(n.same_as) ?? []), n]);
  const out: { note: Note; folded: boolean }[] = [];
  for (const n of notes) {
    if (n.same_as && notes.some((x) => x.id === n.same_as)) continue;
    out.push({ note: n, folded: false });
    for (const m of under.get(n.id) ?? []) out.push({ note: m, folded: true });
  }
  return out;
}

export const agentCommand = (g: Pick<Game, "id">) => `thrdm feedback pull ${g.id.replace(/^g_/, "")}`;

export function gameNotesDash(g: Game): HTMLElement {
  ensureNotesStyle();
  const params = new URLSearchParams(location.search);
  const f: InboxFilter = {
    status: (["open", "addressed", "wontfix", "all"].includes(params.get("status") ?? "") ? params.get("status") : "open") as InboxFilter["status"],
    version: params.get("version") ?? "",
    chip: ((CHIPS as readonly string[]).includes(params.get("chip") ?? "") ? params.get("chip") : "") as Chip | "",
  };
  const host = h("div");
  let restoreKey: string | null = null;
  const load = () => {
    // the filter that was just used gets focus back once the list redraws (keyboard users stay in place)
    restoreKey = (document.activeElement as HTMLElement | null)?.dataset?.key ?? null;
    history.replaceState(null, "", `${location.pathname}?${inboxQuery(f)}`);
    clear(
      host,
      section(null, async (body) => {
        await fill(body, g, f, load);
        if (restoreKey) (body.querySelector(`[data-key="${CSS.escape(restoreKey)}"]`) as HTMLElement | null)?.focus();
      }),
    );
  };
  load();
  return h(
    "div",
    null,
    h("h1", null, `${g.title}: Notes`),
    h("p", { class: "dim" }, "Quick notes players leave after a session: how it felt, what it was about, a line and sometimes a screenshot. They're for you, not reviews; nobody else reads them."),
    agentBox(g, f),
    host,
  );
}

function agentBox(g: Game, f: InboxFilter): HTMLElement {
  const copy = h(
    "button",
    {
      type: "button",
      class: "btn small",
      onclick: async () => {
        try {
          // markdown, not JSON: api() parses JSON, so fetch the export directly
          const r = await fetch(`${API_BASE.community}/games/${g.id}/notes/export?format=md&${inboxQuery(f)}`, { headers: { authorization: `Bearer ${(await accessToken()) ?? ""}` } });
          if (!r.ok) throw new ApiError(r.status, `http_${r.status}`, "Couldn't export the notes just now.");
          await navigator.clipboard.writeText(await r.text());
          toast("Copied: paste it to your coding agent");
        } catch (e) {
          toast(e instanceof Error ? e.message : String(e));
        }
      },
    },
    "Copy for your agent",
  );
  // folded on phones, so the notes themselves are on the first screen; open on wider screens
  const wide = typeof matchMedia === "function" && matchMedia("(min-width: 601px)").matches;
  return h(
    "details",
    { class: "nti-agent", open: wide },
    h("summary", null, h("strong", null, "Hand them to your agent")),
    h("p", { class: "dim small", style: { margin: "0" } }, "Each note has an id. After you ship a fix, mark the notes it answered, and their authors hear \"your note shipped\" once that version is live."),
    h("div", { class: "nti-act" }, copy, h("code", null, agentCommand(g)), h("code", null, "thrdm feedback done <id> --in v1.3")),
  );
}

async function fill(body: HTMLElement, g: Game, f: InboxFilter, reload: () => void): Promise<void> {
  const r = await api<Inbox>("community", `/games/${g.id}/notes?${inboxQuery(f)}`);
  const pick = <K extends keyof InboxFilter>(k: K, v: InboxFilter[K]) => {
    f[k] = v;
    reload();
  };
  const status = h(
    "div",
    { class: "tabs-row", role: "group", "aria-label": "Status" },
    (["open", "addressed", "wontfix", "all"] as const).map((s) =>
      h(
        "button",
        { type: "button", "data-key": `status-${s}`, class: `pill${f.status === s ? " on" : ""}`, "aria-pressed": f.status === s ? "true" : "false", onclick: () => pick("status", s) },
        `${{ open: "Open", addressed: "Addressed", wontfix: "Won't fix", all: "All" }[s]}${s === "all" ? "" : ` ${r.counts[s]}`}`,
      ),
    ),
  );
  const versionSel = h(
    "select",
    { "aria-label": "Version", "data-key": "version", onchange: (e: Event) => pick("version", (e.target as HTMLSelectElement).value) },
    h("option", { value: "" }, "Every version"),
    r.groups.versions.map((v) => h("option", { value: v.version, selected: v.version === f.version }, `${vLabel(v.version)} (${v.notes})`)),
    f.version && !r.groups.versions.some((v) => v.version === f.version) ? h("option", { value: f.version, selected: true }, vLabel(f.version)) : null,
  );
  const groups = h(
    "ul",
    { class: "nti-groups", "aria-label": "By theme" },
    r.groups.chips
      .filter((c) => c.chip !== "general")
      .map((c) =>
        h(
          "li",
          null,
          h(
            "button",
            { type: "button", "data-key": `chip-${c.chip}`, class: `pill${f.chip === c.chip ? " on" : ""}`, "aria-pressed": f.chip === c.chip ? "true" : "false", onclick: () => pick("chip", f.chip === c.chip ? "" : (c.chip as Chip)) },
            CHIP_LABEL[c.chip],
            h("b", null, c.notes),
          ),
        ),
      ),
  );
  const feel = Object.entries(r.groups.feel).filter(([, n]) => n);
  clear(
    body,
    h("div", { class: "nti-bar" }, status, versionSel),
    r.groups.chips.length ? groups : null,
    feel.length ? h("p", { class: "dim small" }, feel.map(([k, n]) => `${n} ${FEEL_LABEL[k as keyof typeof FEEL_LABEL].toLowerCase()}`).join(" · ")) : null,
    r.notes.length
      ? h("ul", { class: "nti-list" }, threaded(r.notes).map(({ note, folded }) => noteItem(note, folded, reload)))
      : h("div", { class: "nt-empty" }, h("p", null, f.status === "open" ? "No open notes." : "Nothing here."), h("p", { class: "dim small" }, "Players leave notes from the Notes tab on your game's page.")),
  );
}

function noteItem(n: Note, folded: boolean, reload: () => void): HTMLElement {
  // "macos · macOS 15.5" says it twice: the platform only when the OS line doesn't already name it
  const platformShown = n.platform && !n.os.toLowerCase().replace(/\s/g, "").includes(n.platform.toLowerCase()) ? n.platform : "";
  const env = [platformShown, n.os, n.gpu].filter(Boolean).join(" · ");
  const shots = (n.media ?? []).map((s) => safeMediaUrl(s.file_url)).filter((u): u is string => !!u);
  return h(
    "li",
    { class: `nti-note${folded ? " folded" : ""}`, id: n.id },
    h(
      "div",
      { class: "nti-top" },
      h("span", { class: "nti-feel" }, h("span", { class: `nt-dot nt-dot-${n.feel}`, "aria-hidden": "true" }), FEEL_LABEL[n.feel]),
      n.chips.map((c) => h("span", { class: "nti-tag" }, CHIP_LABEL[c])),
      h("span", { class: "nti-tag" }, n.version === "live" || !n.version ? "Version unknown" : vLabel(n.version)),
      n.status !== "open" ? h("span", { class: `nti-tag st-${n.status}` }, n.status === "addressed" ? `Addressed in ${n.addressed_in}${n.shipped_at ? ", shipped" : ", waiting for it to go live"}` : "Won't fix") : null,
      n.moderation ? h("span", { class: "nti-tag" }, n.moderation === "hidden" ? "Hidden by moderation" : "Removed") : null,
      n.me_too ? h("span", { class: "nti-metoo" }, `+${n.me_too} said the same`) : null,
      folded ? h("span", { class: "dim small" }, "same as above") : null,
    ),
    n.body ? h("p", { class: "nti-body" }, n.body) : h("p", { class: "nti-body dim" }, "No words, just the tap."),
    shots.length ? h("div", { class: "nti-shots" }, shots.map((u, i) => h("a", { href: u, target: "_blank", rel: "noopener" }, h("img", { src: u, alt: `Screenshot ${i + 1} with this note`, loading: "lazy" })))) : null,
    h("p", { class: "nti-env dim small" }, nameSpan(n.author_id), ` · ${fmtAgo(n.created_at)}${env ? ` · ${env}` : ""}${n.session_seconds ? ` · played ${Math.max(1, Math.round(n.session_seconds / 60))} min` : ""} · `, copyId(n.id)),
    actions(n, reload),
  );
}

/** The note's id (for thrdm feedback done) behind a small button: copied, not read. */
function copyId(id: string): HTMLElement {
  return h(
    "button",
    {
      type: "button",
      class: "nti-copy",
      title: id,
      "aria-label": `Copy note id ${id}`,
      onclick: async () => {
        try {
          await navigator.clipboard.writeText(id);
          toast(`Copied ${id}`);
        } catch {
          toast(id);
        }
      },
    },
    "Copy id",
  );
}

function actions(n: Note, reload: () => void): HTMLElement {
  const set = async (b: Record<string, unknown>, done: string) => {
    try {
      const r = await api<{ shipped: boolean; notified: number }>("community", `/notes/${n.id}/status`, { body: b });
      toast(r.notified ? `${done}: the player has been told` : done);
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  };
  if (n.status !== "open") return h("div", { class: "nti-act" }, h("button", { type: "button", class: "btn small ghost", onclick: () => set({ status: "open" }, "Reopened") }, "Reopen"));
  const form: HTMLFormElement = h(
    "form",
    {
      class: "nti-act",
      onsubmit: (e: Event) => {
        e.preventDefault();
        const fd = new FormData(form);
        const version = String(fd.get("version") ?? "").trim();
        if (!version) return toast("Which version has the fix? For example v1.3");
        void set({ status: "addressed", version, change: String(fd.get("change") ?? "").trim() }, `Addressed in ${version}`);
      },
    },
    h("label", { class: "small dim", for: `v-${n.id}` }, "Fixed in"),
    h("input", { id: `v-${n.id}`, name: "version", maxlength: 64, required: true, autocomplete: "off" }),
    h("label", { class: "sr-only", for: `c-${n.id}` }, "What changed (public, optional)"),
    h("input", { id: `c-${n.id}`, name: "change", placeholder: "What changed (shown on your game's page)", maxlength: 200 }),
    h("button", { type: "submit", class: "btn small" }, "Addressed"),
    h("button", { type: "button", class: "btn small ghost", onclick: () => set({ status: "wontfix" }, "Marked won't fix") }, "Won't fix"),
  );
  return form;
}

/** For tests and the overview: the inbox's lines as text. */
export function inboxSummary(r: Pick<Inbox, "counts" | "groups">): string {
  const top = r.groups.chips.filter((c) => c.chip !== "general").sort((a, b) => b.notes - a.notes)[0];
  return `${r.counts.open} open${top ? `, mostly ${CHIP_LABEL[top.chip].toLowerCase()}` : ""}`;
}

